import React, { useState } from "react";
import { Pencil, Plus, Trash2, Zap } from "lucide-react";

import { AppLogo } from "@/shared/components/AppLogo";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/components/ui/dialog";
import { Switch } from "@/shared/components/ui/switch";
import { Chip, IconButton } from "@/shared/components/ui-kit";
import {
  FieldLabel, GhostButton, PrimaryButton, SelectInput, TextInput,
} from "@/shared/components/settings-atoms";
import {
  useApp, useCreateTrigger, useDeleteTrigger, usePatchConnectionLink, usePatchTrigger, useTriggers,
} from "@/shared/api/apps";

import type { AppArgSpec, AppConnection, AppTriggerSpec, TriggerSubscription } from "@/shared/types";

/* One connected account, as *this agent* uses it (CONTRACT §7).

   Two different things live here because the agent owns both and neither has
   another home: the link — the name the model calls the app by, and whether the
   agent may call it at all — and the subscriptions, which say when an event on
   that account starts a run.

   Every write lands immediately. The panel's Save button belongs to the agent
   draft; a link and a subscription are rows of their own, and a trigger that
   armed at Composio but waited for a Save would fire into an agent that does
   not know about it yet. */

const MINUTE = 60_000;
const STEPS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["day", 1_440 * MINUTE], ["hour", 60 * MINUTE], ["minute", MINUTE],
];
const relative = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

function firedAgo(iso: string): string {
  const ago = Date.now() - new Date(iso).getTime();
  const [unit, span] = STEPS.find(([, ms]) => ago >= ms) ?? STEPS[STEPS.length - 1];
  return relative.format(-Math.round(ago / span), unit);
}

type Draft = { id: string | null; slug: string; config: Record<string, unknown>; prompt: string };

const seedConfig = (spec: AppTriggerSpec): Record<string, unknown> =>
  Object.fromEntries(spec.config.filter((a) => a.def !== undefined).map((a) => [a.name, a.def]));

const filled = (v: unknown) => v !== undefined && v !== null && String(v).trim() !== "";

/** One config argument, rendered from the spec the catalog already ships. */
function ConfigField({ arg, value, onChange }: {
  arg: AppArgSpec;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  // The wire takes `unknown`, so an interval typed as text would arrive as "5".
  const numeric = /int|number|float/i.test(arg.type);
  return (
    <div>
      <FieldLabel>
        {arg.name}
        {arg.req && <span className="text-[var(--danger)]"> *</span>}
        <span className="ml-1.5 font-mono text-[11px] font-normal text-[var(--text-quaternary)]">{arg.type}</span>
      </FieldLabel>
      {arg.values ? (
        <SelectInput
          value={String(value ?? "")}
          onChange={onChange}
          options={[{ id: "", label: "—" }, ...arg.values.map((v) => ({ id: v, label: v }))]}
        />
      ) : (
        <TextInput
          value={String(value ?? "")}
          placeholder={arg.def !== undefined ? String(arg.def) : ""}
          onChange={(v) => onChange(numeric && v.trim() !== "" ? Number(v) : v)}
        />
      )}
      <p className="m-0 mt-1 text-[11.5px] leading-[1.5] text-[var(--text-quaternary)]">{arg.desc}</p>
    </div>
  );
}

/** Add and edit are the same form; only the event picker locks once armed. */
function TriggerForm({ draft, specs, taken, busy, onChange, onCancel, onSave }: {
  draft: Draft;
  specs: AppTriggerSpec[];
  taken: string[];
  busy: boolean;
  onChange: (draft: Draft) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const spec = specs.find((s) => s.slug === draft.slug);
  // Required config and a prompt are both NOT NULL on the row — checked here so
  // the 422 stays the backend's backstop rather than this form's error path.
  const incomplete =
    !draft.prompt.trim() || !!spec?.config.some((a) => a.req && !filled(draft.config[a.name]));

  return (
    <div className="mt-2 rounded-[10px] ring-1 ring-[var(--hairline-strong)] bg-[var(--surface)] p-3.5 space-y-3.5 fade-in">
      <div>
        <FieldLabel>Event</FieldLabel>
        <SelectInput
          value={draft.slug}
          disabled={!!draft.id}
          onChange={(slug) => {
            const next = specs.find((s) => s.slug === slug);
            onChange({ ...draft, slug, config: next ? seedConfig(next) : {} });
          }}
          options={specs.map((s) => ({
            // One subscription per event per account (agent_id, connection_id,
            // trigger_slug is unique), so a taken one is shown, not offered.
            id: s.slug,
            label: taken.includes(s.slug) && s.slug !== draft.slug ? `${s.name} — already added` : s.name,
          }))}
        />
        {spec && <p className="m-0 mt-1 text-[11.5px] leading-[1.5] text-[var(--text-quaternary)]">{spec.desc}</p>}
      </div>

      {spec?.config.map((arg) => (
        <ConfigField
          key={arg.name}
          arg={arg}
          value={draft.config[arg.name]}
          onChange={(v) => onChange({ ...draft, config: { ...draft.config, [arg.name]: v } })}
        />
      ))}

      <div>
        <FieldLabel>Prompt<span className="text-[var(--danger)]"> *</span></FieldLabel>
        <textarea
          value={draft.prompt}
          onChange={(e) => onChange({ ...draft, prompt: e.target.value })}
          rows={3}
          placeholder="What the agent should do with each event…"
          className="w-full resize-none px-3 py-2 rounded-[10px] bg-[var(--canvas)] ring-1 ring-[var(--hairline-strong)] text-[13.5px] leading-[1.6] outline-none focus:ring-[var(--hairline-focus)] placeholder:text-[var(--text-quaternary)]"
        />
        <p className="m-0 mt-1 text-[11.5px] leading-[1.5] text-[var(--text-quaternary)]">
          Each event starts its own run on its own thread. The payload is handed to the agent with this prompt.
        </p>
      </div>

      <div className="flex items-center justify-end gap-2">
        <GhostButton onClick={onCancel}>Cancel</GhostButton>
        <PrimaryButton onClick={onSave} disabled={incomplete || busy}>
          {draft.id ? "Save trigger" : "Add trigger"}
        </PrimaryButton>
      </div>
    </div>
  );
}

/** A live subscription: what it is, whether it is armed, when it last fired. */
function SubscriptionRow({ sub, onEdit }: { sub: TriggerSubscription; onEdit: () => void }) {
  const patch = usePatchTrigger();
  const remove = useDeleteTrigger();

  return (
    <div className="group flex items-center gap-2.5 px-2 py-[7px] rounded-[8px] hover:bg-[var(--surface)] min-w-0">
      <Zap className="w-3.5 h-3.5 shrink-0 text-[var(--text-quaternary)]" strokeWidth={1.75} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-[13px] font-medium tracking-[-0.005em] truncate text-[var(--text-primary)]">
            {sub.trigger?.name ?? sub.trigger_slug}
          </span>
          {/* `enabled` is what we asked Composio for; `external_id` is whether it
              took. An unarmed subscription delivers nothing, so it says so. */}
          {!sub.external_id && <Chip tone="warn">Not armed</Chip>}
        </div>
        <div className="text-[11.5px] text-[var(--text-tertiary)] truncate mt-[1px]">
          {sub.last_event_at ? `Last fired ${firedAgo(sub.last_event_at)}` : "No events yet"}
        </div>
      </div>
      <IconButton onClick={onEdit} title="Edit trigger" className="hidden group-hover:flex">
        <Pencil className="w-3 h-3" strokeWidth={1.75} />
      </IconButton>
      <IconButton
        onClick={() => remove.mutate(sub.id)}
        title="Delete trigger"
        className="hidden group-hover:flex hover:text-[var(--danger)]"
      >
        <Trash2 className="w-3 h-3" strokeWidth={1.75} />
      </IconButton>
      <Switch
        aria-label="Trigger enabled"
        checked={sub.enabled}
        disabled={patch.isPending}
        onCheckedChange={(on) => patch.mutate({ id: sub.id, body: { enabled: on } })}
      />
    </div>
  );
}

export function AppTriggersDialog({ agentId, connection, onOpenChange }: {
  agentId: string;
  /** Carries `link` — this is a connection read with `agent_id`. */
  connection: AppConnection;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: app, isPending: loadingSpec } = useApp(connection.app.slug);
  const { data: allSubs } = useTriggers(agentId);
  const link = usePatchConnectionLink();
  const create = useCreateTrigger();
  const patch = usePatchTrigger();

  const [label, setLabel] = useState(connection.link?.label ?? connection.app.slug);
  const [draft, setDraft] = useState<Draft | null>(null);

  const specs = app?.spec.triggers ?? [];
  const subs = (allSubs ?? []).filter((s) => s.connection_id === connection.id);
  const taken = subs.map((s) => s.trigger_slug);
  const free = specs.filter((s) => !taken.includes(s.slug));

  const saveLabel = () => {
    const next = label.trim();
    if (!next || next === connection.link?.label) return;
    link.mutate({ id: connection.id, agentId, body: { label: next } });
  };

  const save = () => {
    if (!draft) return;
    const body = { config: draft.config, prompt: draft.prompt.trim() };
    const close = () => setDraft(null);
    if (draft.id) patch.mutate({ id: draft.id, body }, { onSuccess: close });
    else {
      create.mutate(
        { connectionId: connection.id, body: { agent_id: agentId, trigger_slug: draft.slug, ...body } },
        { onSuccess: close },
      );
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 w-[480px] max-w-[94vw] rounded-[16px] overflow-hidden bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog">
        <div className="px-5 pt-5 pb-4 flex items-start gap-3">
          <AppLogo logo={connection.app.logo} name={connection.app.name} size={30} iconSize={17} rounded={8} />
          <div className="min-w-0">
            <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
              {connection.app.name}
            </DialogTitle>
            <DialogDescription className="text-[13px] text-[var(--text-tertiary)] mt-0.5 truncate">
              {connection.account_label ?? "Connected account"}
            </DialogDescription>
          </div>
        </div>

        <div className="px-5 pb-5 max-h-[68vh] overflow-y-auto space-y-5">
          <div>
            <FieldLabel>Tool name</FieldLabel>
            <TextInput
              value={label}
              onChange={setLabel}
              onBlur={saveLabel}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
              spellCheck={false}
            />
            <p className="m-0 mt-1 text-[11.5px] leading-[1.5] text-[var(--text-quaternary)]">
              What the model calls this account by — unique within the agent.
            </p>
          </div>

          <div className="flex items-center gap-2.5">
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-medium text-[var(--text-primary)]">Tools available</div>
              <div className="text-[11.5px] text-[var(--text-tertiary)] mt-[1px]">
                Off keeps the triggers armed but takes the tools away from the model.
              </div>
            </div>
            <Switch
              aria-label="Tools available"
              checked={connection.link?.enabled ?? true}
              disabled={link.isPending}
              onCheckedChange={(on) => link.mutate({ id: connection.id, agentId, body: { enabled: on } })}
            />
          </div>

          <div>
            <div className="section-label mb-1.5">Triggers</div>
            {subs.length === 0 && !draft && (
              <p className="m-0 px-2 py-3 text-center text-[12.5px] text-[var(--text-quaternary)]">
                Nothing wakes this agent from {connection.app.name} yet.
              </p>
            )}
            {subs.map((sub) => (
              <SubscriptionRow
                key={sub.id}
                sub={sub}
                onEdit={() =>
                  setDraft({ id: sub.id, slug: sub.trigger_slug, config: { ...sub.config }, prompt: sub.prompt })
                }
              />
            ))}

            {draft ? (
              <TriggerForm
                draft={draft}
                specs={specs}
                taken={taken}
                busy={create.isPending || patch.isPending}
                onChange={setDraft}
                onCancel={() => setDraft(null)}
                onSave={save}
              />
            ) : (
              <button
                onClick={() => free[0] && setDraft({ id: null, slug: free[0].slug, config: seedConfig(free[0]), prompt: "" })}
                disabled={loadingSpec || free.length === 0}
                className="press mt-2 w-full h-9 rounded-[8px] border border-dashed border-[var(--hairline-strong)] text-[13px] text-[var(--text-secondary)] hover:bg-[var(--surface)] hover:border-[var(--text-tertiary)] flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:pointer-events-none"
              >
                <Plus className="w-3.5 h-3.5" strokeWidth={1.75} />
                {loadingSpec
                  ? "Loading events…"
                  : specs.length === 0
                    ? `${connection.app.name} announces no events`
                    : free.length === 0
                      ? "Every event is subscribed"
                      : "Add trigger"}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
